/**
 * BatchController component for managing batch epic analysis operations
 * Provides batch analysis trigger button, progress display, and real-time updates
 */

import React, { useEffect, useState, memo } from 'react'
import type { ReactElement } from 'react'
import { PlayIcon, StopIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import { BatchControllerProps, BatchStatus } from '../../../types/epic-types'
import { useStartBatchAnalysis, useBatchStatus, getBatchRefetchInterval } from '../../../utils/epicApi'
import { Card } from '../../Elements/Cards'
import { LoadingSpinner } from '../../Elements/Indicators'
import { ProgressBar, BatchProgress, LoadingButton } from '../../Elements/ProgressIndicators'

const BatchController = memo(({
  projectKey,
  dateRange,
  canStartBatch,
  activeBatch,
  onBatchStart,
  onBatchCancel
}: BatchControllerProps): ReactElement => {
  const [localBatchId, setLocalBatchId] = useState<string | null>(null)

  // Mutation for starting batch analysis
  const startBatchMutation = useStartBatchAnalysis()

  // Query for batch status (only when we have an active batch)
  const batchStatusQuery = useBatchStatus({
    batchId: activeBatch?.batchId || localBatchId || '',
    enabled: !!(activeBatch?.batchId || localBatchId),
    refetchInterval: activeBatch?.status ? getBatchRefetchInterval(activeBatch.status) || undefined : 5000
  })

  // Update local batch status when query data changes
  useEffect(() => {
    if (batchStatusQuery.data?.success) {
      const transformedStatus = {
        batchId: batchStatusQuery.data.batchId,
        status: batchStatusQuery.data.data.status,
        progress: {
          completed: batchStatusQuery.data.data.summary.completed + batchStatusQuery.data.data.summary.failed,
          total: batchStatusQuery.data.data.summary.totalEpics,
          percentage: batchStatusQuery.data.data.metrics.progressPercentage
        },
        estimatedTimeRemaining: batchStatusQuery.data.data.metrics.estimatedTimeRemaining,
        metrics: {
          processingRate: batchStatusQuery.data.data.metrics.processingRate,
          averageProcessingTime: batchStatusQuery.data.data.metrics.averageProcessingTime,
          successRate: batchStatusQuery.data.data.metrics.successRate,
          elapsedTime: batchStatusQuery.data.data.metrics.elapsedTime
        }
      } as BatchStatus

      // Clear local batch ID when batch completes
      if (transformedStatus.status === 'completed' || transformedStatus.status === 'failed') {
        setLocalBatchId(null)
      }
    }
  }, [batchStatusQuery.data])

  // Handle batch start
  const handleStartBatch = async () => {
    if (!projectKey || !dateRange.startDate || !dateRange.endDate) {
      return
    }

    try {
      const result = await startBatchMutation.mutateAsync({
        projectKey,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        batchName: `${projectKey} Epic Analysis - ${new Date().toLocaleDateString()}`
      })

      if (result.success && result.batchId) {
        setLocalBatchId(result.batchId)
        onBatchStart({
          projectKey,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          batchName: `${projectKey} Epic Analysis - ${new Date().toLocaleDateString()}`
        })
      }
    } catch (error) {
      console.error('Failed to start batch analysis:', error)
    }
  }

  // Handle batch cancel
  const handleCancelBatch = () => {
    if (activeBatch?.batchId && onBatchCancel) {
      onBatchCancel(activeBatch.batchId)
      setLocalBatchId(null)
    }
  }

  // Determine current batch status (from props or local query)
  const currentBatch = activeBatch || (batchStatusQuery.data?.success ? {
    batchId: batchStatusQuery.data.batchId,
    status: batchStatusQuery.data.data.status,
    progress: {
      completed: batchStatusQuery.data.data.summary.completed + batchStatusQuery.data.data.summary.failed,
      total: batchStatusQuery.data.data.summary.totalEpics,
      percentage: batchStatusQuery.data.data.metrics.progressPercentage
    },
    estimatedTimeRemaining: batchStatusQuery.data.data.metrics.estimatedTimeRemaining
  } as BatchStatus : null)

  // Check if batch is actively processing
  const isProcessing = currentBatch && (currentBatch.status === 'queued' || currentBatch.status === 'processing')
  const isCompleted = currentBatch?.status === 'completed'
  const isFailed = currentBatch?.status === 'failed'

  // Determine if we can start a new batch
  const canStart = canStartBatch && !isProcessing && !startBatchMutation.isLoading

  return (
    <Card className="p-6 h-full flex flex-col" role="region" aria-labelledby="batch-analysis-heading">
      {/* Status indicator - only show for completed/failed states */}
      <div className="flex justify-end">
        {isCompleted ? (
          <div className="flex items-center text-green-600">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Completed</span>
          </div>
        ) : null}

        {isFailed ? (
          <div className="flex items-center text-red-600">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Failed</span>
          </div>
        ) : null}
      </div>

      {/* Batch controls */}
      <div className="flex-1 flex items-center">
        {!currentBatch ? (
          <div className="flex items-center justify-between w-full gap-4">
            <p className="text-gray-600 text-sm flex-1">
              Analyze all unanalyzed epics in the selected project and date range.
            </p>
            <div className="flex-shrink-0">
              <LoadingButton
                onClick={handleStartBatch}
                disabled={!canStart}
                isLoading={startBatchMutation.isLoading}
                loadingText="Starting..."
                className={`
                  px-4 py-2 border border-transparent text-sm font-medium rounded-md
                  ${canStart && !startBatchMutation.isLoading
                    ? 'text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  }
                  transition-colors duration-150
                `}
                type="button"
                aria-describedby="batch-start-help"
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                Start Batch Analysis
              </LoadingButton>
            </div>
          </div>
        ) : (
          <div className="w-full">
            <BatchProgress
              total={currentBatch.progress.total}
              completed={currentBatch.progress.completed}
              failed={0} // We'll need to track failed separately if needed
              label={isProcessing ? "Analyzing Epics" : isCompleted ? "Analysis Complete" : "Analysis Failed"}
              showDetails={true}
              estimatedTimeRemaining={currentBatch.estimatedTimeRemaining ?
                parseInt(currentBatch.estimatedTimeRemaining.replace(/[^\d]/g, '')) || undefined :
                undefined}
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Error display */}
      {startBatchMutation.error ? (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-800 text-sm">
            Failed to start batch analysis: {startBatchMutation.error instanceof Error ? startBatchMutation.error.message : 'Unknown error'}
          </p>
        </div>
      ) : null}

      {batchStatusQuery.error ? (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-yellow-800 text-sm">
            Unable to fetch batch status: {batchStatusQuery.error instanceof Error ? batchStatusQuery.error.message : 'Unknown error'}
          </p>
        </div>
      ) : null}
    </Card>
  )
})

BatchController.displayName = 'BatchController'

export default BatchController